// Hook to apply feature-specific mode settings
// This hook syncs the module's configured mode from Feature Settings to ModeProvider

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMode } from '@/providers/ModeProvider';
import { getModuleIdForRoute, getModuleMode, canModuleUseLiveMode } from '@/lib/feature-mode';
import type { FeatureMode } from '@/types/features';

// Hook to get and apply the feature mode for a specific module
export function useFeatureMode(moduleId: string): {
  mode: FeatureMode;
  isDemo: boolean;
  isLive: boolean;
  canUseLive: boolean;
} {
  const { mode, setMode, isDemo, isLive } = useMode();
  const canUseLive = canModuleUseLiveMode(moduleId);
  
  useEffect(() => {
    const featureMode = getModuleMode(moduleId);
    // Only update if the mode is different to avoid unnecessary re-renders
    if (mode !== featureMode) {
      setMode(featureMode);
    }
  }, [moduleId, setMode, mode]);
  
  return {
    mode: mode as FeatureMode,
    isDemo,
    isLive,
    canUseLive,
  };
}

// Hook to automatically apply feature mode based on current route
// Use this in AppLayout to automatically sync mode when navigating between pages
export function useApplyFeatureModeForRoute(): {
  currentModuleId: string | null;
  mode: FeatureMode;
  isDemo: boolean;
  isLive: boolean;
  canUseLive: boolean;
} {
  const location = useLocation();
  const { mode, setMode, isDemo, isLive } = useMode();
  const currentModuleId = getModuleIdForRoute(location.pathname);
  const canUseLive = currentModuleId ? canModuleUseLiveMode(currentModuleId) : true;
  
  useEffect(() => {
    if (currentModuleId) {
      const featureMode = getModuleMode(currentModuleId);
      // Only update if the mode is different to avoid unnecessary re-renders
      if (mode !== featureMode) {
        setMode(featureMode);
      }
    }
  }, [currentModuleId, setMode, mode]);
  
  return {
    currentModuleId,
    mode: mode as FeatureMode,
    isDemo,
    isLive,
    canUseLive,
  };
}
